import { Client, IMessage } from '@stomp/stompjs';

import { EOptionSymbolSide, ISymbolTickRulesGroup } from '@aqx-core/common/api/market';
import { EHorizonExecuteType, ENotificationOrderStateCentroid } from '@aqx-core/common/api/notification';
import { EFillPolicyValue, EOrderStatus, EOrderType } from '@aqx-core/common/api/order';
import { EAlertCondition, EAlertFrequency } from '@aqx-core/common/api/price-alert';
import { ETradingState } from '@aqx-core/common/api/user-account';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum EMetatraderMessageType {
  ORDER = 'ORDER',
  BALANCE = 'BALANCE',
  NOTIFICATION = 'NOTIFICATION',
  ONE_CLICK = 'ONE_CLICK',
  ORDER_REJECTED = 'ORDER_REJECTED',
  PRICE_ALERT = 'PRICE_ALERT'
}

export interface IUseMetaTraderTopicParams {
  low: number;
  high: number;
  buy: number;
  sell: number;
}

export interface IMetaTraderNotificationMessageData {
  T: number; // time
}

export interface IMetaTraderOrderMessageData {
  /** Horizon Fix Execution Report */
  O: string; // orderId - HZ5_...
  T: number; // type
  C: EOrderType; // cmd - TradeRcordCmd /OrderType
  S: string; // symbol
  OP: number; // openPrice
  CP: number; // closePrice
  V: number; // volume
  MT4: number; // metatraderId
  SL: number; // stopLoss
  TP: number; // takeProfit
  P: number; // profit
  TR: number; // tradeId - NOT USE
  ST: number; // orderState
  CMT: string; // comment - reason
  HOS: EOrderStatus; // horizonOrderStatus - enmum
  HET: EHorizonExecuteType; // horizonExecuteType
  Sbs: EOptionSymbolSide; // symbolSide
  LS: number; // lotSize
  FLS: number; // filledLotSize
  TIF: EFillPolicyValue; // timeInForce
  Pri: number; // price
  StP: number; // stopPrice
  AFP: number; // avgFillPrice
  OpT: number; // openTime - timestamps
  ExT: number; // expireTime - timestamps
  TRG: ISymbolTickRulesGroup[];
  // Centroid
  CNOS?: ENotificationOrderStateCentroid;
}

export interface IMetaTraderBalanceMessageData {
  T: number; // time
  C: EOrderType; // order type
  B: number; // balance
  M: number; // margin
  E: number; // equity
  FM: number; // free margin
  PL: number; // profit or loss
  ML: number; // margin level
  MC: number; // margin call
  MSO: number; // margin stop out
  N: number; // notional
  CM: number; // commission
  S: number; // swaps
  AW: number; // available withdraw
  TS: ETradingState; // trading state
  CD: number; // credit
}

export interface IMetaTraderOrderMessage {
  M: EMetatraderMessageType.ORDER;
  D: IMetaTraderOrderMessageData;
}

export interface IMetaTraderBalanceMessage {
  M: EMetatraderMessageType.BALANCE;
  D: IMetaTraderBalanceMessageData;
}

export interface IMetaTraderNotificationMessage {
  M: EMetatraderMessageType.NOTIFICATION;
  D: IMetaTraderNotificationMessageData;
}

export interface IMetaTraderOrderRejectedMessage {
  M: EMetatraderMessageType.ORDER_REJECTED;
  D: IMetaTraderOrderMessageData;
}

export interface IMetaTraderOneClickMessage {
  M: EMetatraderMessageType.ONE_CLICK;
  D: IMetaTraderNotificationMessageData;
}

export interface IMetaTraderPriceAlertRawData {
  AI: string; // alertId
  S: string; // symbol
  TI: string; // traderId
  MI: string; // metatraderId
  TP: number; // targetPrice
  C: EAlertCondition; // condition
  FC: EAlertFrequency; // frequencyCondition
}

export interface IMetaTraderPriceAlertMessage {
  M: EMetatraderMessageType.PRICE_ALERT;
  D: IMetaTraderPriceAlertRawData;
}

export type IMetaTraderMessage =
  | IMetaTraderOrderMessage
  | IMetaTraderBalanceMessage
  | IMetaTraderNotificationMessage
  | IMetaTraderOrderRejectedMessage
  | IMetaTraderOneClickMessage
  | IMetaTraderPriceAlertMessage;

export interface IMetaTraderReceiver {
  (payload: IMetaTraderMessage | undefined): void;
}

interface ISubscribeParams {
  receiver: IMetaTraderReceiver;
  tradeSubscription: string;
}

interface IProps {
  client: Client;
}

class MetaTraderTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IMetaTraderReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { receiver, tradeSubscription } = params;

    const { subPath, topicId } = this.mapTopicIds(tradeSubscription);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { receiver, tradeSubscription } = params;

    const { subPath, topicId } = this.mapTopicIds(tradeSubscription);

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as IMetaTraderMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = (tradeSubscription: string) => {
    const subPath = `metatrader/${tradeSubscription}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default MetaTraderTopic;
